// Chemin: C:\gestion-copro\src\components\messagerie\ArchiveEmailModal.tsx
// Session 38 : Intégration SENTINELLE pour pré-remplir les champs
// Session 44 : Archivage automatique Gmail (label + retrait inbox)
// Session 126 : Transmission categorieGED (taxonomie GED v2) à archiveEmail

import React, { useState, useEffect } from 'react';
import { X, Archive, Paperclip, Loader2, FolderOpen, Sparkles } from 'lucide-react';
import { EmailDetail } from '../../types/gmail.types';
import { Affaire } from '../../types/affaire.types';
import { TYPE_DOCUMENT_LABELS, TypeDocument } from '../../types/document.types';
import { getAffairesByCopropriete } from '../../services/affaire.service';
import { archiveEmail } from '../../services/document.service';
import { analyzeEmail, EmailSuggestion } from '../../services/emailAnalysis.service';
import { archiveEmailInGmail } from '../../services/gmail.service';
import { useAuth } from '../../context/AuthContext';

interface ArchiveEmailModalProps {
  email: EmailDetail;
  coproprieteId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ArchiveEmailModal: React.FC<ArchiveEmailModalProps> = ({
  email,
  coproprieteId,
  onClose,
  onSuccess
}) => {
  const { firebaseUser, userProfile } = useAuth();
  const [titre, setTitre] = useState(email.subject || 'Email sans objet');
  const [categorie, setCategorie] = useState<TypeDocument | ''>('');
  const [categorieGED, setCategorieGED] = useState(''); // Session 126 : Catégorie GED (SYN, PRO, GPA...)
  const [affaireId, setAffaireId] = useState('');
  const [affaires, setAffaires] = useState<Affaire[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAffaires, setLoadingAffaires] = useState(true);
  const [error, setError] = useState('');

  // États SENTINELLE
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null);

  // Charger les affaires au montage
  useEffect(() => {
    const loadAffaires = async () => {
      try {
        const data = await getAffairesByCopropriete(coproprieteId);
        // Filtrer uniquement les affaires ouvertes ou en cours
        setAffaires(data.filter((a: Affaire) => a.statut === 'ouverte' || a.statut === 'en_cours'));
      } catch (err) {
        console.error('Erreur chargement affaires:', err);
      } finally {
        setLoadingAffaires(false);
      }
    };
    loadAffaires();
  }, [coproprieteId]);

  // Lancer l'analyse SENTINELLE au montage
  useEffect(() => {
    const runAnalysis = async () => {
      setAnalyzing(true);
      try {
        const result = await analyzeEmail(
          coproprieteId,
          email.subject || '',
          email.from || '',
          email.fromEmail || '',
          email.bodyText || email.body || '',
          email.date.toISOString()
        );

        if (result) {
          setSuggestion(result);
          // Pré-remplir les champs avec les suggestions
          if (result.typeDocument) {
            setCategorie(result.typeDocument as TypeDocument);
          }
          // Session 126 : Pré-remplir la catégorie GED
          if (result.categorie) {
            setCategorieGED(result.categorie);
          }
          if (result.affaireId) {
            setAffaireId(result.affaireId);
          }
        }
      } catch (err) {
        console.error('Erreur analyse SENTINELLE:', err);
        // On continue sans suggestion, pas d'erreur bloquante
      } finally {
        setAnalyzing(false);
      }
    };

    runAnalysis();
  }, [coproprieteId, email]);

  // Pré-sélectionner toutes les PJ par défaut
  useEffect(() => {
    if (email.attachments && email.attachments.length > 0) {
      setSelectedAttachments(email.attachments.map(a => a.id));
    }
  }, [email.attachments]);

  const toggleAttachment = (attachmentId: string) => {
    setSelectedAttachments(prev =>
      prev.includes(attachmentId)
        ? prev.filter(id => id !== attachmentId)
        : [...prev, attachmentId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categorie) {
      setError('Veuillez sélectionner une catégorie');
      return;
    }

    if (!firebaseUser) {
      setError('Utilisateur non connecté');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await archiveEmail({
        coproprieteId,
        email,
        titre,
        categorie,
        categorieGED: categorieGED || undefined, // Session 126 : Catégorie GED v2
        affaireId: affaireId || undefined,
        selectedAttachmentIds: selectedAttachments,
        userId: firebaseUser.uid,
        userNom: userProfile?.prenom && userProfile?.nom 
          ? `${userProfile.prenom} ${userProfile.nom}` 
          : 'Utilisateur'
      });
      
      // Archiver aussi dans Gmail (ajouter label + retirer de inbox)
      try {
        await archiveEmailInGmail(coproprieteId, email.id);
      } catch (gmailErr) {
        // Log mais ne bloque pas - l'archivage principal a réussi
        console.warn('Archivage Gmail échoué (non bloquant):', gmailErr);
      }
      
      onSuccess();
    } catch (err) {
      console.error('Erreur archivage:', err);
      setError('Erreur lors de l\'archivage. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const hasAttachments = email.attachments && email.attachments.length > 0;

  // Convertir TYPE_DOCUMENT_LABELS en tableau pour le select
  const categoriesArray = Object.entries(TYPE_DOCUMENT_LABELS).map(([value, label]) => ({
    value: value as TypeDocument,
    label
  }));

  // Badge de confiance
  const getConfidenceBadge = (confidence: 'haute' | 'moyenne' | 'basse') => {
    const styles = {
      haute: 'bg-green-100 text-green-800',
      moyenne: 'bg-yellow-100 text-yellow-800',
      basse: 'bg-gray-100 text-gray-600',
    };
    const labels = {
      haute: 'Confiance haute',
      moyenne: 'Confiance moyenne',
      basse: 'Confiance basse',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[confidence]}`}>
        {labels[confidence]}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Archiver l'email</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Bandeau SENTINELLE */}
        {analyzing && (
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-700">SENTINELLE analyse l'email...</span>
          </div>
        )}

        {suggestion && !analyzing && (
          <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Suggestion SENTINELLE</span>
              {getConfidenceBadge(suggestion.confidence)}
            </div>
            <p className="text-xs text-purple-700">{suggestion.explication}</p>
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre du document
            </label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catégorie <span className="text-red-500">*</span>
              {suggestion?.typeDocument && categorie === suggestion.typeDocument && (
                <span className="ml-2 text-xs text-purple-600">(suggéré par SENTINELLE)</span>
              )}
            </label>
            <select
              value={categorie}
              onChange={(e) => setCategorie(e.target.value as TypeDocument)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                suggestion?.typeDocument && categorie === suggestion.typeDocument
                  ? 'border-purple-300 bg-purple-50'
                  : 'border-gray-300'
              }`}
              required
            >
              <option value="">Sélectionner une catégorie</option>
              {categoriesArray.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Affaire liée */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-1">
                <FolderOpen className="w-4 h-4" />
                Rattacher à une affaire (optionnel)
                {suggestion?.affaireId && affaireId === suggestion.affaireId && (
                  <span className="ml-2 text-xs text-purple-600">(suggéré)</span>
                )}
              </div>
            </label>
            {loadingAffaires ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Chargement des affaires...
              </div>
            ) : (
              <select
                value={affaireId}
                onChange={(e) => setAffaireId(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  suggestion?.affaireId && affaireId === suggestion.affaireId
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-gray-300'
                }`}
              >
                <option value="">Aucune affaire</option>
                {affaires.map(affaire => (
                  <option key={affaire.id} value={affaire.id}>
                    {affaire.numero} - {affaire.titre}
                  </option>
                ))}
              </select>
            )}
            {suggestion?.affaireNom && !affaireId && (
              <p className="text-xs text-purple-600 mt-1">
                SENTINELLE suggère : {suggestion.affaireNom}
              </p>
            )}
          </div>

          {/* Pièces jointes */}
          {hasAttachments && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-1">
                  <Paperclip className="w-4 h-4" />
                  Pièces jointes à archiver
                </div>
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {email.attachments!.map(attachment => (
                  <label
                    key={attachment.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAttachments.includes(attachment.id)}
                      onChange={() => toggleAttachment(attachment.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 truncate flex-1">
                      {attachment.filename}
                    </span>
                    <span className="text-xs text-gray-400">
                      {Math.round(attachment.size / 1024)} Ko
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Résumé email */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="text-gray-500 mb-1">Email source :</div>
            <div className="text-gray-700">
              <div><span className="font-medium">De :</span> {email.from}</div>
              <div><span className="font-medium">Date :</span> {new Date(email.date).toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</div>
            </div>
          </div>

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !categorie || analyzing}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Archivage...
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4" />
                  Archiver
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArchiveEmailModal;
